import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, finalize, of, shareReplay, tap } from 'rxjs';
import { CACHE_TTL_MS } from 'src/app/core/http/http-context.tokens';
import { HttpCacheStore } from 'src/app/core/http/http-cache.store';
import { UserStateService } from 'src/app/shared/services/user-state.service';

/**
 * GETs that mutate server state — never cacheable, mirroring RetryInterceptor's denylist.
 */
const SIDE_EFFECTING_GET_PATTERN =
  /\/delete-notification|\/send-mail|\/resend-email|\/transaction-fail-notify|\/nonce|\/signature-nonce|\/transak\/create-widget-url|\/idenfy\//;

@Injectable()
export class CacheInterceptor implements HttpInterceptor {

  constructor(
    private readonly store: HttpCacheStore,
    private readonly userState: UserStateService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const ttl = req.context.get(CACHE_TTL_MS);

    if (ttl <= 0 || !this.isCacheable(req)) {
      return next.handle(req);
    }

    const key = this.buildKey(req);

    const cached = this.store.get(key);
    if (cached) {
      // Short-circuits above ProcessingInterceptor, so a hit costs no spinner flash.
      return of(cached.clone());
    }

    const inFlight = this.store.getPending(key);
    if (inFlight) {
      return inFlight;
    }

    const request$ = next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.store.set(key, event.clone(), ttl);
        }
      }),
      finalize(() => this.store.clearPending(key)),
      shareReplay({ bufferSize: 1, refCount: true })
    ) as Observable<HttpEvent<unknown>>;

    this.store.setPending(key, request$ as Observable<HttpResponse<unknown>>);
    return request$;
  }

  private isCacheable(req: HttpRequest<unknown>): boolean {
    if (req.method.toUpperCase() !== 'GET') {
      return false;
    }
    return !SIDE_EFFECTING_GET_PATTERN.test(req.url);
  }

  /**
   * RegulatedInterceptor runs ahead of this one and makes the *same URL* return
   * different bodies per custody mode and per chain, with credentials attached. Keying
   * on the URL alone would serve one user's dashboard to the next on a shared device,
   * so identity is part of the key, not an afterthought.
   */
  private buildKey(req: HttpRequest<unknown>): string {
    const regulated = req.headers.get('regulated') ?? 'unknown';
    const chainId = req.headers.get('Chain-id') ?? 'unknown';
    const userId = this.userState.user?._id ?? 'anonymous';

    return [req.method, req.urlWithParams, regulated, chainId, userId].join('|');
  }
}
